import { FC } from "react";
import { Link } from "react-router";
import { Formik, Form } from "formik";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";

import { Button } from "../../../../components";
import { ERoutes } from "../../../../constants/routes";
import AuthLayout from "../../../../layout/AuthLayout/AuthLayout";
import { registrationSchema } from "../../../../utils/validators";
import { RegisterPayload } from "../../../../store/auth/types";
import {
  selectAuthSelectedCompetitorsRegister,
  selectAuthLoadingRegister,
  selectAuthErrorRegister,
  selectAuthCompany
} from "../../../../store/selectors/auth";
import OAuthButton from "../../../../components/OAuthButton/OAuthButton";
import FormikInputField from "../../../../components/FormikField/FormikInputField/FormikInputField";
import CardPreview from "./Components/CardPreview/CardPreview";
import { PREVIEW_SCORES, PREVIEW_AI_MODELS } from "./constant";
import { registerUser } from "../../../../store/auth/slice";
import { TERMS_URL } from "../../../../constants/config";
import { PRIVACY_URL } from "../../../../constants/config";
import StackItems from "../../../StackItems/StackItems";

import styles from "./Step3.module.css";



const Step3: FC = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoadingRegister);
  const error = useSelector(selectAuthErrorRegister);
  const company = useSelector(selectAuthCompany);
  const competitors = useSelector(selectAuthSelectedCompetitorsRegister);

  const handleRegister = (values: RegisterPayload) => {
    dispatch(registerUser(values));
  };

  const renderLeftSide = () => {
    if (!company || !competitors) {
      return <div>No competitors</div>;
    }

    return (
      <div className={styles.preview__wrapper}>
        <div className={styles.preview}>
        <h3 className={styles.preview__title}>AI score is almost done</h3>

        {[company, ...competitors.slice(0, 3)].map((item, index) => (
          <div key={index} className={styles.preview__card}>
            <CardPreview name={item.name} scores={PREVIEW_SCORES[index]} />
          </div>
        ))}

        <div className={styles.preview__footer}>
          <div className={styles.preview__item}>
            <div>
              <p>Competitors</p>
            </div>
            <div>
              <p>{competitors.length} / Unlimited</p>
            </div>
          </div>
          <div className={styles.preview__item}>
            <div>
              <p>AI models</p>
              <StackItems items={PREVIEW_AI_MODELS} count={5} />
            </div>
            <div>
              <p>14 / 14</p>
            </div>
          </div>
          <div className={styles.preview__item}>
            <div>
              <p>Daily requests</p>
            </div>
            <div>
              <p>36 / Unlimited</p>
            </div>
          </div>
          <div className={styles.preview__tags}>
            <div className={classNames(styles.tag, styles["is-green"])}>
              <img src="/tags-icon/digest.svg" alt="Email" />
              <p>Weekly email digest</p>
            </div>
            <div className={classNames(styles.tag, styles["is-yellow"])}>
              <img src="/tags-icon/trial.svg" alt="Email" />
              <p>14-day trial</p>
            </div>
            <div className={classNames(styles.tag, styles["is-purple"])}>
              <img src="/tags-icon/cancel.svg" alt="Email" />
              <p>Cancel any time</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AuthLayout leftSide={renderLeftSide()}>
      <div className={styles.register}>

        <header className={styles.register__header}>
          <h1 className={styles.register__title}>Create an Account</h1>
        </header>

        <OAuthButton titleGoogle="Join with Google" titleMicrosoft="Join with Microsoft" isRegister isDisabled={loading} />

        <div className={styles.divider}>
          <hr />
          <span>OR SIGN UP WITH EMAIL</span>
          <hr />
        </div>

        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            password: ""
          }}
          validationSchema={registrationSchema}
          onSubmit={(val) => handleRegister(val)}
        >
          {() => (
            <Form className={styles.form}>
              <div className={styles.form__fields}>
                <div className={styles.form__field}>
                  <FormikInputField
                    placeholder="First name"
                    type="text"
                    name="firstName"
                    id="firstName"
                  />
                </div>
                <div className={styles.form__field}>
                  <FormikInputField
                    placeholder="Last name"
                    type="text"
                    name="lastName"
                    id="lastName"
                  />
                </div>
              </div>

              <div className={styles.form__field}>
                <FormikInputField
                  placeholder="Email"
                  type="email"
                  name="email"
                  id="email"
                />
              </div>
              <div className={styles.form__field}>
                <FormikInputField
                  placeholder="Password"
                  type="password"
                  name="password"
                  id="password"
                />
              </div>

              <p className={styles.form__agreement}>
                By clicking this button, you agree to our <Link target="_blank" to={TERMS_URL}>Terms of Service</Link> and
                <Link target="_blank" to={PRIVACY_URL}> Privacy policy</Link>.
              </p>

              <Button
                size="lg"
                type="submit"
                disabled={loading}
                className={styles.form__submit}
                label="Create an account"
                typeStyle="fill-auth"
              />
              {error && <div className={styles.form__error}>{error}</div>}
            </Form>
          )}
        </Formik>

        <footer className={styles.register__footer}>
          <p className={styles.register__caption}>Already have an AICARMA account? <Link to={ERoutes.Login}>Log in</Link></p>
        </footer>
      </div>
    </AuthLayout>
  );
};

export default Step3;